<template>
  <div ref="emailItemEl">
    <div v-if="open" class="px-4 py-3 space-y-4">
      <div>
        <div
          class="flex items-center justify-between cursor-pointer"
          @click="emit('email:click')"
        >
          <div v-tooltipable class="text-headline-new font-medium">
            <span v-if="email.sender?.name">{{ email.sender.name }}</span>
          </div>
          <div class="flex items-center gap-x-2">
            <provider-image
              :provider="iconInfo.provider"
              alt="Email Provider Icon"
            />
            <date-time
              :date="email.created_at"
              class="shrink-0 whitespace-nowrap"
            />
          </div>
        </div>
        <div v-if="email.receiver?.name" v-tooltipable class="text-label">
          {{ $t("COMMON.TO") }}: {{ email.receiver.name }}
        </div>
      </div>
      <div v-if="email.content_text" class="text-headline">
        {{ email.content_text }}
      </div>
      <div
        v-if="!email.is_outgoing"
        class="flex cursor-pointer w-fit-content items-center justify-start gap-x-2 text-primary"
        @click="showReplyModal = true"
      >
        <icon-base :icon="IconReply" />
        <span>{{ $t("COMMON.REPLY") }}</span>
      </div>
      <div v-if="email.status_message" class="flex space-x-1">
        <icon-base :icon="IconErrorWarning" class="text-error" />
        <span class="text-error">{{ email.status_message }}</span>
      </div>
      <send-deal-communication-modal
        v-if="showReplyModal"
        :deal-id="activeDeal.id"
        :type="CommunicationType.email"
        :deal-name="businessName"
        :reply-to-message="email.content_text ?? ''"
        @close-dialog="showReplyModal = false"
      />
    </div>
    <div
      v-else
      class="flex items-center justify-between px-4 py-3 gap-x-4 cursor-pointer"
      @click="handleEmailOpening"
    >
      <div
        v-if="email.sender?.name"
        v-tooltipable
        class="max-w-30 truncate text-headline-new font-medium"
      >
        {{ email.sender.name }}
      </div>
      <div v-if="email.content_text" v-tooltipable class="flex-1 truncate">
        {{ email.content_text }}
      </div>
      <date-time :date="email.created_at" class="shrink-0 whitespace-nowrap" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef } from "vue";

import { CommunicationType } from "@/enums/communicationLogs";
import { DealActivityState } from "@/enums/dealActivity";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import { useEmailLogInfo } from "@/hooks/communicationLogs";
import { useDeals } from "@/hooks/deals";
import { useLocalStorageSetting } from "@/hooks/options";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import ProviderImage from "@/components/communicationLogs/communication/ProviderImage.vue";
import IconErrorWarning from "@/components/icons/IconErrorWarning.vue";
import IconReply from "@/components/icons/IconReply.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";

import type { EmailThreadItem } from "@/models/communicationLogs";

interface Props {
  email: EmailThreadItem;
  businessName: string | undefined;
  open?: boolean;
}

const { email, businessName, open = false } = defineProps<Props>();

const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);

const { activeDeal } = useDeals();
const emailItemEl = useTemplateRef("emailItemEl");
const { iconInfo } = useEmailLogInfo(email);

const emit = defineEmits<{
  "email:click": [];
}>();

const showReplyModal = ref(false);

const scrollItemToView = () => {
  if (dealActivityOptions.value.state !== DealActivityState.Static) {
    return;
  }

  emailItemEl.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

const handleEmailOpening = async () => {
  emit("email:click");

  await nextTick();

  scrollItemToView();
};

onMounted(() => {
  if (open) {
    scrollItemToView();
  }
});
</script>
